import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../../utils/axios";
import { toast } from "react-hot-toast";
import { IoIosEyeOff, IoIosEye } from "react-icons/io";
import loginImage from "../../Assets/Images/login/login.png";
import logo from "../../Assets/Logos/logo.jpg";
import CryptoJS from "crypto-js";
import Swal from "sweetalert2";

const AdminLogin = () => {
  const navigate = useNavigate();
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [showSuggestion, setShowSuggestion] = useState(false);
  const [errors, setErrors] = useState({ identifier: "", password: "" });
  const [isForgotPasswordMode, setIsForgotPasswordMode] = useState(false);
  const [forgotPasswordData, setForgotPasswordData] = useState({
    email: "",
    phoneNumber: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [forgotLoading, setForgotLoading] = useState(false);

  const userType = "admin";
  const toastId = "toaster";

  // Secret key for AES encryption
  const SECRET_KEY = "mySuperSecretKey123";

  // Check token + admin data on mount
  useEffect(() => {
    const token = localStorage.getItem("token");
    const adminData = localStorage.getItem("admin-data");

    if (token && adminData) {
      try {
        const admin = JSON.parse(adminData);

        if (admin?._id || admin?.id) {
          navigate("/dashboard", { replace: true });
        }
      } catch (err) {
        console.error("Invalid admin data in localStorage");
        localStorage.clear();
      }
    }

    // Load remembered creds if present
    const savedIdentifier = localStorage.getItem("rememberedIdentifier");
    const savedPassword = localStorage.getItem("rememberedPassword");

    if (savedIdentifier && savedPassword) {
      try {
        const decryptedPassword = CryptoJS.AES.decrypt(
          savedPassword,
          SECRET_KEY
        ).toString(CryptoJS.enc.Utf8);

        setIdentifier(savedIdentifier);
        setPassword(decryptedPassword);
        setRememberMe(true);
        setShowSuggestion(true);
      } catch (err) {
        console.error("Password decryption failed");
      }
    }
  }, [navigate]);

  const validateForm = () => {
    let isValid = true;
    const newErrors = { identifier: "", password: "" };

    if (!identifier) {
      newErrors.identifier = "Email or mobile number is required.";
      isValid = false;
    } else if (
      !/^\d{10}$/.test(identifier) &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(identifier)
    ) {
      newErrors.identifier = "Enter a valid email or 10-digit mobile number.";
      isValid = false;
    }

    if (!password) {
      newErrors.password = "Password is required.";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      setIsLoading(true);
      const { data } = await axiosInstance.post(`/admins/admin-login`, {
        identifier,
        password,
        userType,
      });

      const { token, admin } = data;

      localStorage.setItem("token", token);
      localStorage.setItem("admin-data", JSON.stringify(admin));

      // Save encrypted credentials only after successful login
      if (rememberMe) {
        const encryptedPassword = CryptoJS.AES.encrypt(
          password,
          SECRET_KEY
        ).toString();
        localStorage.setItem("rememberedIdentifier", identifier);
        localStorage.setItem("rememberedPassword", encryptedPassword);
      } else {
        localStorage.removeItem("rememberedIdentifier");
        localStorage.removeItem("rememberedPassword");
      }

      Swal.fire({
        icon: "success",
        title: "Login Successful",
        text: "Redirecting to dashboard...",
        timer: 2000,
        showConfirmButton: false,
      });
      setTimeout(() => {
        navigate("/dashboard");
        setIsLoading(false);
      }, 2000);
    } catch (error) {
      const message =
        error.response?.data?.message || "Login failed. Please try again.";
      toast.error(message, { id: toastId });
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPasswordChange = (e) => {
    const { name, value } = e.target;
    setForgotPasswordData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleForgotPasswordSubmit = async (e) => {
    e.preventDefault();
    const { email, phoneNumber, newPassword, confirmPassword } =
      forgotPasswordData;

    if (!email || !phoneNumber || !newPassword || !confirmPassword) {
      toast.error("Please fill in all fields");
      return;
    }

    if (newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    setForgotLoading(true);
    const toastId = toast.loading("Resetting password...");

    try {
      await axiosInstance.post(`/admins/admin-forgot-password`, {
        email,
        phoneNumber,
        newPassword,
      });
      toast.success("Password reset successful", { id: toastId });

      setForgotPasswordData({
        email: "",
        phoneNumber: "",
        newPassword: "",
        confirmPassword: "",
      });
      setIsForgotPasswordMode(false);
    } catch (error) {
      const message =
        error.response?.data?.message || "Reset failed. Try again.";
      toast.error(message, { id: toastId });
    } finally {
      setForgotLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100 px-4 py-6">
      <div className="w-full max-w-6xl bg-white rounded-xl shadow-xl flex flex-col md:flex-row overflow-hidden">
        <div className="hidden md:block w-full md:w-1/2">
          <img
            src={loginImage}
            alt="Login Illustration"
            className="w-full h-full object-cover"
          />
        </div>

        <div className="w-full md:w-1/2 px-6 sm:px-8 md:px-10 py-8 sm:py-12 flex flex-col justify-center">
          <img src={logo} alt="Logo" className="w-40 sm:w-48 mb-6 mx-auto" />
          <h2 className="text-2xl font-sans text-center mb-1">
            Hi, Welcome Back
          </h2>
          <p className="text-center text-gray-500 text-sm mb-8">
            {isForgotPasswordMode
              ? "Reset your password"
              : "Enter your credentials to continue"}
          </p>

          {!isForgotPasswordMode ? (
            <form onSubmit={handleLogin} className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email or Mobile Number
                </label>
                <input
                  type="text"
                  value={identifier}
                  onChange={(e) => setIdentifier(e.target.value)}
                  className={`w-full px-4 py-2 text-sm border ${
                    errors.identifier ? "border-red-500" : "border-gray-300"
                  } rounded-md`}
                  placeholder="Enter your email or mobile number"
                />
                {errors.identifier && (
                  <p className="text-xs text-red-500 mt-1">
                    {errors.identifier}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className={`w-full px-4 py-2 text-sm border ${
                      errors.password ? "border-red-500" : "border-gray-300"
                    } rounded-md`}
                    placeholder="Enter your password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-3 flex items-center text-gray-500"
                  >
                    {showPassword ? <IoIosEyeOff /> : <IoIosEye />}
                  </button>
                </div>
                {errors.password && (
                  <p className="text-xs text-red-500 mt-1">{errors.password}</p>
                )}
              </div>

              <div className="flex items-center justify-between text-sm text-gray-600">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                  />
                  Remember Me
                </label>
                <button
                  type="button"
                  onClick={() => setIsForgotPasswordMode(true)}
                  className="hover:underline"
                >
                  Forgot Password?
                </button>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-[#0F172A] text-white py-2 rounded-md disabled:opacity-70"
              >
                {isLoading ? "Loading..." : "Continue"}
              </button>
            </form>
          ) : (
            <form onSubmit={handleForgotPasswordSubmit} className="space-y-5">
              <input
                type="email"
                name="email"
                placeholder="Email"
                value={forgotPasswordData.email}
                onChange={handleForgotPasswordChange}
                className="w-full px-4 py-2 text-sm border border-gray-300 rounded-md"
              />
              <input
                type="text"
                name="phoneNumber"
                placeholder="Phone Number"
                value={forgotPasswordData.phoneNumber}
                onChange={handleForgotPasswordChange}
                className="w-full px-4 py-2 text-sm border border-gray-300 rounded-md"
              />
              <div className="relative">
                <input
                  type={showNewPassword ? "text" : "password"}
                  name="newPassword"
                  placeholder="New Password"
                  value={forgotPasswordData.newPassword}
                  onChange={handleForgotPasswordChange}
                  className="w-full px-4 py-2 text-sm border border-gray-300 rounded-md"
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute inset-y-0 right-3 flex items-center text-gray-500"
                >
                  {showNewPassword ? <IoIosEyeOff /> : <IoIosEye />}
                </button>
              </div>
              <div className="relative">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  name="confirmPassword"
                  placeholder="Confirm Password"
                  value={forgotPasswordData.confirmPassword}
                  onChange={handleForgotPasswordChange}
                  className="w-full px-4 py-2 text-sm border border-gray-300 rounded-md"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute inset-y-0 right-3 flex items-center text-gray-500"
                >
                  {showConfirmPassword ? <IoIosEyeOff /> : <IoIosEye />}
                </button>
              </div>
              <div className="flex justify-between gap-4">
                <button
                  type="button"
                  onClick={() => setIsForgotPasswordMode(false)}
                  className="w-1/2 border border-gray-400 py-2 rounded-md text-sm"
                >
                  Back
                </button>
                <button
                  type="submit"
                  disabled={forgotLoading}
                  className="w-1/2 bg-[#0F172A] text-white py-2 rounded-md text-sm disabled:opacity-70"
                >
                  {forgotLoading ? "Resetting..." : "Reset Password"}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
